// AuthService — domain mapping over AuthApi (contract 06 §3). Turns flat wire DTOs into typed
// domain results (the LoginResult discriminated union is the reference pattern) and validates
// server response completeness. It SURFACES ApiError (never raw axios) to the flow, which maps
// it to AuthError. Server owns MFA attempts/expiry/lockout/cooldown — never invented here.
import { AuthApi, authApi } from '../api/AuthApi'
import type {
  AcceptInvitesResponse,
  ChangePasswordCodePayload,
  ChangePasswordPayload,
  ChangePasswordPayloadV2,
  ChangeSettingsPasswordPayload,
  CreateFirstAccountResponse,
  LoginCredentials,
  LoginResult,
  MfaChallenge,
  MfaMethod,
  MfaResendPayload,
  MfaVerifyPayload,
  MfaVerifyResponse,
  PendingInvite,
  ShellHandoffResponse,
  SignUpCredentials,
  TokenHandoffExchangeResponse,
  TotpConfirmPayload,
  TotpSetup,
  TotpSetupResponse,
  TotpStatus,
  TotpStatusResponse,
} from '../models/auth.types'

import type { SessionAccessSection } from '@/features/account'

const MFA_METHODS: readonly MfaMethod[] = ['email', 'totp']

/** Validate + copy the server-authored challenge metadata; throw if the server sent it incomplete. */
function toChallenge(res: MfaChallenge): MfaChallenge {
  const validAttempts = Number.isInteger(res.attempts_remaining) && res.attempts_remaining >= 0
  const validExpiry =
    typeof res.expires_at === 'string' && !Number.isNaN(Date.parse(res.expires_at))
  const validResend =
    res.resend_available_at === undefined ||
    (typeof res.resend_available_at === 'string' &&
      !Number.isNaN(Date.parse(res.resend_available_at)))
  if (
    typeof res.temp_token !== 'string' ||
    !res.temp_token ||
    !validAttempts ||
    !validExpiry ||
    !validResend
  ) {
    throw new Error('Incomplete MFA challenge from server')
  }
  const challenge: MfaChallenge = {
    temp_token: res.temp_token,
    attempts_remaining: res.attempts_remaining,
    expires_at: res.expires_at,
  }
  if (res.resend_available_at !== undefined) challenge.resend_available_at = res.resend_available_at
  // Unknown method names are dropped, not trusted: the screen only ever branches on a known one.
  const methods = Array.isArray(res.mfa_methods)
    ? res.mfa_methods.filter((method): method is MfaMethod => MFA_METHODS.includes(method))
    : []
  if (methods.length > 0) challenge.mfa_methods = methods
  return challenge
}

function toTotpStatus(res: TotpStatusResponse): TotpStatus {
  if (!res || typeof res.enabled !== 'boolean') throw new Error('Malformed authenticator status')
  return {
    enabled: res.enabled,
    confirmedAt: typeof res.confirmed_at === 'string' ? res.confirmed_at : null,
  }
}

/** The error text never echoes the response: `secret` and `otpauth_uri` are credentials. */
function toTotpSetup(res: TotpSetupResponse): TotpSetup {
  if (
    !res ||
    typeof res.secret !== 'string' ||
    !res.secret ||
    typeof res.otpauth_uri !== 'string' ||
    !res.otpauth_uri ||
    (res.confirm_with !== 'password' && res.confirm_with !== 'email_code')
  ) {
    throw new Error('Malformed authenticator setup')
  }
  return { secret: res.secret, otpauthUri: res.otpauth_uri, confirmWith: res.confirm_with }
}

/** Login V2 must carry the account-selection input before the bearer is published. */
function toSessionAccess(value: unknown): SessionAccessSection {
  if (!value || typeof value !== 'object') throw new Error('Incomplete login session access')
  const section = value as Partial<SessionAccessSection>
  if (
    !section.user ||
    typeof section.user !== 'object' ||
    !section.accessible_accounts ||
    !Array.isArray(section.accessible_accounts.data) ||
    !section.user_access_info ||
    typeof section.user_access_info !== 'object'
  ) {
    throw new Error('Incomplete login session access')
  }
  return section as SessionAccessSection
}

export class AuthService {
  constructor(private readonly api: AuthApi = authApi) {}

  async login(creds: LoginCredentials): Promise<LoginResult> {
    const res = await this.api.login(creds)
    if ('mfa_required' in res && res.mfa_required) {
      return { type: 'mfa', challenge: toChallenge(res) }
    }
    if (!res.mfa_required && res.token) {
      return {
        type: 'success',
        token: res.token,
        sessionAccess: toSessionAccess(res.session_access),
        ...(res.frontend_origin ? { frontendOrigin: res.frontend_origin } : {}),
        ...(res.handoff ? { handoff: res.handoff } : {}),
      }
    }
    throw new Error('Malformed login response')
  }

  /** Signup and Shopify register both land here (payload carries optional shop/shopify_context). */
  async signup(payload: SignUpCredentials): Promise<MfaChallenge> {
    return toChallenge(await this.api.register(payload))
  }

  /** MFA verify returns the bearer. `code` stays the exact string the caller passed. */
  async verifyMfaResult(payload: MfaVerifyPayload): Promise<MfaVerifyResponse> {
    const res = await this.api.verifyMfa(payload)
    if (!res.token) throw new Error('Malformed MFA verify response')
    return res
  }

  async verifyMfa(payload: MfaVerifyPayload): Promise<string> {
    return (await this.verifyMfaResult(payload)).token
  }

  /** Resend replaces the WHOLE challenge from the server response. */
  async resendMfa(payload: MfaResendPayload): Promise<MfaChallenge> {
    return toChallenge(await this.api.resendMfa(payload))
  }

  async logout(): Promise<void> {
    await this.api.logout()
  }

  /** Known and unknown addresses both resolve the same (no user enumeration). */
  async requestResetLink(email: string): Promise<void> {
    await this.api.forgotPassword({ email })
  }

  /** Exchange the one-time `#reset_handoff` fragment for a memory-only reset token. */
  async exchangeResetHandoff(resetHandoff: string): Promise<string> {
    const res = await this.api.exchangeResetHandoff({ reset_handoff: resetHandoff })
    if (!res.reset_token) throw new Error('Malformed reset handoff response')
    return res.reset_token
  }

  async changePassword(payload: ChangePasswordPayload | ChangePasswordCodePayload): Promise<void> {
    await this.api.changePassword(payload)
  }

  async submitTeamJoinPassword(payload: ChangePasswordPayloadV2): Promise<void> {
    await this.api.teamJoinSetPassword(payload)
  }

  /** Settings password change. No domain mapping to do — the route answers with no body — so this
   *  exists purely to keep `AuthApi` feature-internal (contract 06 §4) for the settings caller. */
  async changeSettingsPassword(payload: ChangeSettingsPasswordPayload): Promise<void> {
    await this.api.changeSettingsPassword(payload)
  }

  async getTotpStatus(): Promise<TotpStatus> {
    return toTotpStatus(await this.api.getTotpStatus())
  }

  /** Starts (or replaces) the pending authenticator setup; the secret never leaves the caller. */
  async startTotpSetup(): Promise<TotpSetup> {
    return toTotpSetup(await this.api.setupTotp())
  }

  async confirmTotp(payload: TotpConfirmPayload): Promise<TotpStatus> {
    return toTotpStatus(await this.api.confirmTotp(payload))
  }

  async disableTotp(): Promise<TotpStatus> {
    return toTotpStatus(await this.api.disableTotp())
  }

  /** Exchange the one-time `?handoff` for a bearer (+ optional account). Opaque, ≤60s, single-use. */
  async exchangeTokenHandoff(handoff: string): Promise<TokenHandoffExchangeResponse> {
    const res = await this.api.exchangeTokenHandoff({ handoff })
    if (!res.token) throw new Error('Malformed token handoff response')
    return res
  }

  /**
   * The MCP shell's one-time sign-in key (ORP-27464) for the selected account. `null` when the
   * server answered without one — the caller then sends the user to the shell's own login.
   */
  async requestShellHandoff(
    accountId: number | string | null,
  ): Promise<ShellHandoffResponse | null> {
    const res = await this.api.requestShellHandoff({ account_id: accountId })
    return typeof res?.handoff === 'string' && res.handoff ? res : null
  }

  async requestInviteAccess(accountId: string, adminEmail: string): Promise<void> {
    await this.api.requestInviteAccess(accountId, { email: adminEmail })
  }

  async getPendingInvites(): Promise<PendingInvite[]> {
    const response = await this.api.getPendingInvites()
    return Array.isArray(response.invites) ? response.invites : []
  }

  async acceptPendingInvites(inviteIds: number[]): Promise<AcceptInvitesResponse> {
    const response = await this.api.acceptPendingInvites(inviteIds)
    return {
      accepted: Array.isArray(response.accepted) ? response.accepted : [],
      failed: Array.isArray(response.failed) ? response.failed : [],
    }
  }

  async createFirstAccount(): Promise<CreateFirstAccountResponse> {
    return this.api.createFirstAccount()
  }
}

export const authService = new AuthService()
